'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { ArrowLeft, Home, Mail, RefreshCw } from 'lucide-react';
import { captureError } from '@focal-point/error-tracking/client';
import { publicConfig } from '@/lib/env';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    captureError(error, {
      tags: { type: 'page-error' },
      extra: { digest: error.digest },
    });
  }, [error]);

  return (
    <div className="min-h-screen bg-sand text-ink flex items-center justify-center px-6">
      <div className="max-w-2xl w-full text-center">
        {/* Error Icon */}
        <div className="mb-8 flex justify-center">
          <div className="w-20 h-20 bg-danger/10 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-danger"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink mb-4">
          Something Went Wrong
        </h1>
        <p className="text-xl text-body mb-8">
          We&apos;ve been notified and are working on a fix. Please try again.
        </p>

        {/* Error Details (Development Only) */}
        {publicConfig.isDevelopment && (
          <div className="mb-8 p-4 bg-danger/10 border border-danger/30 rounded-lg text-left">
            <p className="text-sm font-mono text-danger">
              {error.message || 'Unknown error'}
            </p>
            {error.digest && (
              <p className="text-xs text-caption mt-2">
                Error ID: {error.digest}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <button
            onClick={reset}
            className="group flex flex-col items-center gap-3 p-6 bg-card hover:bg-sand-2 rounded-lg border border-line hover:border-ink-blue transition-all duration-300"
          >
            <RefreshCw className="w-8 h-8 text-ink-blue group-hover:scale-110 transition-transform" />
            <span className="font-semibold">Try Again</span>
          </button>

          <Link
            href="/"
            className="group flex flex-col items-center gap-3 p-6 bg-card hover:bg-sand-2 rounded-lg border border-line hover:border-ink-blue transition-all duration-300"
          >
            <Home className="w-8 h-8 text-ink-blue group-hover:scale-110 transition-transform" />
            <span className="font-semibold">Go Home</span>
          </Link>

          <Link
            href="/contact"
            className="group flex flex-col items-center gap-3 p-6 bg-card hover:bg-sand-2 rounded-lg border border-line hover:border-ink-blue transition-all duration-300"
          >
            <Mail className="w-8 h-8 text-ink-blue group-hover:scale-110 transition-transform" />
            <span className="font-semibold">Contact Us</span>
          </Link>
        </div>

        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center gap-2 text-caption hover:text-ink-blue transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Go back to previous page
        </button>
      </div>
    </div>
  );
}
